import { useState, useEffect } from "react";
import config from "../config";

// In-memory cache to avoid repeated API calls
let cachedMaterialTypes = null;
let cachePromise = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useMaterialTypes = () => {
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaterialTypes = async () => {
      const now = Date.now();

      // Return cached data if available and fresh
      if (cachedMaterialTypes && now - lastFetchTime < CACHE_DURATION) {
        console.log(
          `Using cached material types (age: ${Math.round(
            (now - lastFetchTime) / 1000
          )}s)`
        );
        setMaterialTypes(cachedMaterialTypes);
        setLoading(false);
        return;
      }

      // If a fetch is already in progress, wait for it
      if (cachePromise) {
        try {
          const types = await cachePromise;
          setMaterialTypes(types);
          setLoading(false);
          return;
        } catch (err) {
          setError(err.message);
          setLoading(false);
          return;
        }
      }

      // Start new fetch
      setLoading(true);

      cachePromise = (async () => {
        try {
          console.log("Fetching material types from API...");

          const response = await fetch(
            `${config.apiUrl}/material_management/types`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
              credentials: "include",
            }
          );

          if (!response.ok) {
            let errorMessage = "Failed to fetch material types.";
            try {
              const data = await response.json();
              errorMessage = data.message || errorMessage;
            } catch (jsonError) {
              console.error("Could not parse error response:", jsonError);
            }

            if (response.status === 401) {
              console.error("Unauthorized - token may be expired");
              throw new Error("Session expired. Please log in again.");
            } else if (response.status >= 500) {
              throw new Error("Server error. Please try again later.");
            }

            throw new Error(errorMessage);
          }

          const data = await response.json();
          // Now expects array of objects: [{ name, material_category }, ...]
          const types = data.material_types || [];

          // Update cache
          cachedMaterialTypes = types;
          lastFetchTime = Date.now();
          cachePromise = null;

          console.log(
            `Fetched ${types.length} material types from API and cached`
          );

          return types;
        } catch (err) {
          cachePromise = null;
          throw err;
        }
      })();

      try {
        const types = await cachePromise;
        setMaterialTypes(types);
        setError(null);
      } catch (err) {
        console.error("Error fetching material types:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterialTypes();
  }, []);

  return { materialTypes, loading, error };
};

// Export function to clear cache if needed
export const clearMaterialTypesCache = () => {
  cachedMaterialTypes = null;
  cachePromise = null;
  lastFetchTime = 0;
  console.log("Material types cache cleared");
};
